<script>
import { getNumber } from '@utils/Format'
import {
  isOffshoreInvestmentType,
  isOnshoreInvestmentType,
} from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'

export default {
  name: 'InfoCarteira',
  components: { FeedbackWrapper },
  props: {
    idCliente: {
      type: [Number, String],
      required: false,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    investmentType: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clienteBenchmarkMeta: {
      type: Object,
      default: null,
    },
    welcomeData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      verMaisDistribuicao: false,
      limiteDistribuicao: 5,
    }
  },
  computed: {
    cardData() {
      return this.welcomeData
    },
    nomeFormatado() {
      if (!this.cardData || !this.cardData.nomeCliente) {
        return 'Possui total investido de:'
      }

      const name = this.cardData.nomeCliente
      const nameLower = name.toLocaleLowerCase()

      if (nameLower.startsWith('cliente')) {
        return `O ${name} possui total investido de:`
      }
      return `O cliente ${name} possui total investido de:`
    },
    coresRentabilidade() {
      const defaultColors = {
        ano: 'var(--neutral-700)',
        mes: 'var(--neutral-700)',
      }

      if (!this.cardData) return defaultColors

      const { ano, mes } = this.cardData.rentabilidade

      return {
        ano: this.getCorRentabilidade(ano),
        mes: this.getCorRentabilidade(mes),
      }
    },
    distribuicaoExibida() {
      if (!this.cardData) return []
      const distribuicao = this.cardData.distribuicao
      if (this.verMaisDistribuicao) {
        return distribuicao
      } else {
        return distribuicao.slice(0, this.limiteDistribuicao)
      }
    },
    noDataState() {
      if (this.loading) return false

      const hasClient = !!this.idCliente
      if (!hasClient) return true

      const processedData = this.cardData
      const hasRelevantData = this.hasRelevantClientData({ processedData })

      return !hasRelevantData
    },
    benchmarkLabel() {
      if (!this.clienteBenchmarkMeta) return 'CDI'
      return this.clienteBenchmarkMeta.label || 'CDI'
    },
    benchmarkIsTimesLogic() {
      if (!this.clienteBenchmarkMeta) return true
      return (this.clienteBenchmarkMeta.logic || 'times') === 'times'
    },
    emptyStateMessage() {
      return !this.idCliente
        ? 'Nenhum cliente selecionado'
        : 'Sem dados para exibir no momento'
    },
    isOffshoreInvestment() {
      return isOffshoreInvestmentType({
        investmentType: this.investmentType,
      })
    },
    isOnshoreInvestment() {
      return isOnshoreInvestmentType({
        investmentType: this.investmentType,
      })
    },
  },
  methods: {
    hasRelevantClientData({ processedData }) {
      if (!processedData) return false
      const hasTotal = !!processedData.totalInvestido
      const hasDistribution =
        Array.isArray(processedData.distribuicao) &&
        processedData.distribuicao.length > 0
      const rentability = processedData.rentabilidade || {}
      const hasRentabilityValue =
        !!rentability.total || !!rentability.ano || !!rentability.mes
      const hasFlags = processedData.temRentabilidade === false

      return hasTotal || hasDistribution || hasRentabilityValue || hasFlags
    },
    getCorRentabilidade(valor) {
      const num = getNumber(valor)
      if (Number.isNaN(num)) return 'var(--neutral-700)'
      if (num > 0) return 'var(--success)'
      if (num < 0) return 'var(--danger)'
      return 'var(--neutral-700)'
    },
    cadastrarMovimentacao() {
      this.$emit('open-new-movement')
    },
    alternarVerMais() {
      this.verMaisDistribuicao = !this.verMaisDistribuicao
    },
  },
}
</script>

<template>
  <div
    class="info-carteira az-card-bordered"
    :class="{ 'info-carteira--loading': loading }"
  >
    <FeedbackWrapper
      :loading="loading"
      :noData="noDataState"
      :message="emptyStateMessage"
      :minHeight="245"
    >
      <div v-if="cardData">
        <div class="info-carteira__layout">
          <div class="info-carteira__cliente">
            <div class="az-text-primary az-size-14 font-medium az-muted mb-4">
              {{ nomeFormatado }}
            </div>

            <div class="info-carteira__cliente-box">
              <div class="info-carteira__cliente-saldo">
                <div class="az-text-primary az-size-16 font-semibold">
                  {{ cardData.totalInvestido }}
                </div>
              </div>

              <div class="distribuicao-section">
                <span class="az-text-primary az-size-12 font-semibold">
                  Distribuído nas instituições
                </span>

                <div class="info-carteira__cliente-lista mt-2">
                  <div
                    v-for="(item, index) in distribuicaoExibida"
                    :key="index"
                    class="az-text-primary az-size-12 font-semibold az-muted"
                  >
                    <span>{{ item.percentual }}% {{ item.instituicao }}</span>
                  </div>

                  <div v-if="cardData.distribuicao.length > limiteDistribuicao">
                    <BaseButton
                      text
                      sm
                      class="info-carteira__cliente-button"
                      @click="alternarVerMais"
                    >
                      {{ verMaisDistribuicao ? 'Ver menos' : 'Ver mais' }}
                    </BaseButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="cardData.temRentabilidade"
            class="info-carteira__rentabilidade"
          >
            <div class="az-text-primary az-size-14 font-medium az-muted mb-4">
              A rentabilidade total da carteira
            </div>
            <div class="info-carteira__rentabilidade-lista">
              <div class="az-text-primary az-size-14 az-muted">
                <strong>{{ cardData.rentabilidade.total }}%</strong>
                <span v-if="benchmarkIsTimesLogic">
                  do {{ benchmarkLabel }}</span
                >
                <span v-else> vs {{ benchmarkLabel }}</span>
              </div>
              <div class="az-text-primary az-size-14 az-muted">
                <span>Ano:</span>
                <span :style="{ color: coresRentabilidade.ano }">
                  {{ cardData.rentabilidade.ano }}%
                </span>
                <span v-if="benchmarkIsTimesLogic">
                  do {{ benchmarkLabel }}</span
                >
                <span v-else> vs {{ benchmarkLabel }}</span>
              </div>
              <div class="az-text-primary az-size-14 az-muted">
                <span>Mês:</span>
                <span :style="{ color: coresRentabilidade.mes }">
                  {{ cardData.rentabilidade.mes }}%
                </span>
                <span v-if="benchmarkIsTimesLogic">
                  do {{ benchmarkLabel }}</span
                >
                <span v-else> vs {{ benchmarkLabel }}</span>
              </div>
            </div>
          </div>

          <div v-else class="info-carteira__sem-movimentacoes">
            <p
              class="info-carteira__sem-movimentacoes-texto az-text-primary az-size-14"
            >
              {{
                readonly
                  ? 'Não há histórico de ativos cadastrados.'
                  : 'Você não cadastrou o histórico de ativos para o seu cliente.'
              }}
            </p>
            <BaseButton
              v-if="!readonly"
              primary
              outline
              fitContent
              @click="cadastrarMovimentacao"
            >
              Cadastrar movimentação
            </BaseButton>
          </div>
        </div>
      </div>
    </FeedbackWrapper>
  </div>
</template>

<style lang="sass" scoped>
.info-carteira
  padding-bottom: 32px

  &--loading
    padding: 0 !important

    ::v-deep .feedback-wrapper > div
      height: 100% !important
      width: 100% !important

  &__layout
    display: flex
    height: 100%
    justify-content: space-between
    align-items: flex-start
    position: relative

    &::before
      content: ''
      position: absolute
      top: 0
      bottom: 0
      left: 50%
      transform: translateX(-50%)
      width: 1px
      background-color: var(--border-hairline)

  &__cliente
    width: 50%
    height: 100%

    &-box
      display: flex
      gap: 46px

    &-saldo
      min-width: fit-content

    &-button
      min-width: 64px !important

    &-lista
      display: flex
      flex-direction: column
      gap: 8px

  &__rentabilidade,
  &__sem-movimentacoes
    width: 50%
    padding-left: 42px

  &__rentabilidade
    &-lista
      display: flex
      flex-direction: column
      gap: 4px

  &__sem-movimentacoes
    min-width: 300px

    &-texto
      max-width: 250px
</style>
